import React, { useRef, useState } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { X, UploadSimple, CheckCircle, WarningCircle, DownloadSimple } from "@phosphor-icons/react";
import { STAGES } from "../lib/stages";

const SAMPLE = `name,email,phone,company,title,website,notes,tags
Jane Doe,jane@acme.com,+15551112222,Acme,VP Sales,acme.com,Met at SaaStr,warm-lead
Rajesh Patel,rajesh@beta.co,+14082223333,Beta Labs,Founder,,Intro from Sam,referral`;

/**
 * CSV Import modal.
 *   - Paste CSV or upload a file.
 *   - Optional event assignment + default stage.
 *   - Shows import summary (created / skipped / errors) with detail lists.
 *   - Backend enforces plan quota → 402 triggers the global UpgradeModal too,
 *     but here we also show which rows were skipped due to cap.
 */
export default function CsvImportModal({ open, onClose, onImported, events }) {
  const [csvText, setCsvText] = useState("");
  const [eventId, setEventId] = useState("");
  const [defaultStage, setDefaultStage] = useState("new");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  if (!open) return null;

  const handleFile = (f) => {
    if (!f) return;
    const reader = new FileReader();
    reader.onload = (ev) => setCsvText(ev.target.result || "");
    reader.readAsText(f);
  };

  const submit = async () => {
    if (!csvText.trim()) { toast.error("Paste or upload a CSV first"); return; }
    setBusy(true); setResult(null);
    try {
      const payload = { csv_text: csvText, default_stage: defaultStage };
      if (eventId) payload.event_id = eventId;
      const r = await api.post("/contacts/import", payload);
      setResult(r.data);
      toast.success(`${r.data.created} contact${r.data.created === 1 ? "" : "s"} imported`);
      if (onImported) onImported(r.data);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Import failed";
      // 402 will already open the global UpgradeModal — surface a short toast too
      toast.error(String(msg).slice(0, 120));
    } finally { setBusy(false); }
  };

  const downloadSample = () => {
    const blob = new Blob([SAMPLE], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = "nexus-contacts-sample.csv"; a.click();
    URL.revokeObjectURL(url);
  };

  const close = () => { setResult(null); setCsvText(""); onClose(); };

  return (
    <div
      className="fixed inset-0 z-[90] flex items-center justify-center p-4"
      style={{ background: "rgba(0,0,0,0.72)", backdropFilter: "blur(6px)" }}
      data-testid="csv-import-modal"
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-2xl border relative" style={{ background: "var(--surface-1)", borderColor: "var(--border)", maxHeight: "92vh", overflowY: "auto" }}>
        <button onClick={close} data-testid="csv-close-btn" className="absolute top-3 right-3 p-2 hover:bg-[#15303A]" aria-label="Close"><X weight="bold" /></button>
        <div className="p-8">
          <div className="overline mb-2">// bulk import</div>
          <h2 className="font-display font-black text-3xl tracking-tighter mb-2">Import contacts from CSV</h2>
          <p className="text-sm mb-6" style={{ color: "var(--text-2)" }}>
            Required column: <code>name</code>. Optional: <code>email, phone, company, title, website, notes, tags</code>.
          </p>

          {!result && (
            <>
              <div className="flex flex-wrap gap-3 mb-4">
                <button onClick={() => fileRef.current?.click()} data-testid="csv-upload-btn" className="btn-secondary flex items-center gap-2">
                  <UploadSimple weight="bold" /> Upload file
                </button>
                <button onClick={downloadSample} data-testid="csv-sample-btn" className="btn-secondary flex items-center gap-2">
                  <DownloadSimple weight="bold" /> Sample CSV
                </button>
                <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="csv-file-input" />
              </div>

              <label className="label-base">Or paste CSV</label>
              <textarea
                value={csvText}
                onChange={(e) => setCsvText(e.target.value)}
                rows={9}
                className="input-base font-mono text-xs"
                style={{ border: "1px solid var(--border)", padding: "10px 12px" }}
                placeholder={SAMPLE}
                data-testid="csv-textarea"
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                <div>
                  <label className="label-base">Assign event (optional)</label>
                  <select value={eventId} onChange={(e) => setEventId(e.target.value)} className="input-base" style={{ background: "var(--surface-2)", border: "1px solid var(--border)", padding: "10px 12px" }} data-testid="csv-event-select">
                    <option value="">— no event —</option>
                    {(events || []).map(ev => <option key={ev.id} value={ev.id}>{ev.name}</option>)}
                  </select>
                </div>
                <div>
                  <label className="label-base">Default stage</label>
                  <select value={defaultStage} onChange={(e) => setDefaultStage(e.target.value)} className="input-base" style={{ background: "var(--surface-2)", border: "1px solid var(--border)", padding: "10px 12px" }} data-testid="csv-stage-select">
                    {STAGES.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                  </select>
                </div>
              </div>

              <div className="flex gap-3 mt-6">
                <button onClick={submit} disabled={busy || !csvText.trim()} data-testid="csv-import-btn" className="btn-primary flex items-center gap-2">
                  {busy ? "Importing..." : "Import contacts"}
                </button>
                <button onClick={close} className="btn-secondary">Cancel</button>
              </div>
            </>
          )}

          {result && (
            <div data-testid="csv-result" className="space-y-4">
              <div className="grid grid-cols-3 gap-0 border-l border-t" style={{ borderColor: "var(--border)" }}>
                <ResultCell label="Created" value={result.created} testid="csv-created" accent />
                <ResultCell label="Skipped" value={result.skipped} testid="csv-skipped" />
                <ResultCell label="Errors" value={result.errors} testid="csv-errors" />
              </div>
              {result.quota_hit && (
                <div className="border p-4 flex items-start gap-3" style={{ borderColor: "var(--accent)", background: "rgba(45,212,212,0.06)" }}>
                  <WarningCircle size={22} weight="fill" color="var(--accent)" />
                  <div className="text-sm">
                    <b>Plan cap reached mid-import.</b> Some rows were skipped. Upgrade your plan and re-import the remaining rows.
                  </div>
                </div>
              )}
              {result.details?.errors?.length > 0 && (
                <details className="border p-3" style={{ borderColor: "var(--border)" }}>
                  <summary className="cursor-pointer text-sm font-semibold">Errors ({result.details.errors.length})</summary>
                  <ul className="mt-3 text-xs font-mono" style={{ color: "var(--text-2)" }}>
                    {result.details.errors.map((e, i) => (
                      <li key={i}>row {e.row}: {e.reason}</li>
                    ))}
                  </ul>
                </details>
              )}
              {result.details?.skipped?.length > 0 && (
                <details className="border p-3" style={{ borderColor: "var(--border)" }}>
                  <summary className="cursor-pointer text-sm font-semibold">Skipped ({result.details.skipped.length})</summary>
                  <ul className="mt-3 text-xs font-mono" style={{ color: "var(--text-2)" }}>
                    {result.details.skipped.map((s, i) => (
                      <li key={i}>row {s.row}: {s.name} — {s.reason}</li>
                    ))}
                  </ul>
                </details>
              )}
              <div className="flex gap-3 pt-2">
                <button onClick={() => { setResult(null); setCsvText(""); }} data-testid="csv-import-more-btn" className="btn-secondary">Import more</button>
                <button onClick={close} data-testid="csv-done-btn" className="btn-primary flex items-center gap-2"><CheckCircle weight="bold" /> Done</button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function ResultCell({ label, value, accent, testid }) {
  return (
    <div className="border-r border-b p-4" style={{ borderColor: "var(--border)" }} data-testid={testid}>
      <div className="overline">{label}</div>
      <div className="font-display font-black text-3xl mt-1" style={{ color: accent ? "var(--accent)" : "#fff" }}>{value}</div>
    </div>
  );
}
